// DatePicker component using react-datepicker
// Styled to match the original Flatpickr appearance

import { forwardRef, useCallback } from 'react'
import ReactDatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'

interface DatePickerProps {
  value: string // ISO date string (YYYY-MM-DD)
  onChange: (value: string) => void
  id?: string
  name?: string
  placeholder?: string
  required?: boolean
  className?: string
  minDate?: string // ISO date string
  maxDate?: string // ISO date string
  disabled?: boolean
  filterDate?: (date: Date) => boolean // Return true to allow the date
}

// Custom input component to match original styling
const CustomInput = forwardRef<HTMLInputElement, {
  value?: string
  onClick?: () => void
  placeholder?: string
  id?: string
  name?: string
  required?: boolean
  disabled?: boolean
}>(({ value, onClick, placeholder, id, name, required, disabled }, ref) => {
  const isFilled = value && value.trim() !== ''

  return (
    <div className="date-picker-wrapper">
      <input
        type="text"
        ref={ref}
        value={value}
        onClick={onClick}
        onChange={() => {}} // Controlled by react-datepicker
        placeholder={placeholder || 'Select date'}
        id={id}
        name={name}
        required={required}
        disabled={disabled}
        readOnly
        className={`date-picker-input${isFilled ? ' field-filled' : ''}`}
      />
      <span className="date-picker-icon">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
          <line x1="16" y1="2" x2="16" y2="6"></line>
          <line x1="8" y1="2" x2="8" y2="6"></line>
          <line x1="3" y1="10" x2="21" y2="10"></line>
        </svg>
      </span>
    </div>
  )
})

CustomInput.displayName = 'CustomInput'

export function DatePicker({
  value,
  onChange,
  id = 'date',
  name = 'date',
  placeholder = 'Select date',
  required = false,
  className = '',
  minDate,
  maxDate,
  disabled = false,
  filterDate,
}: DatePickerProps) {
  // Convert ISO string to Date object for react-datepicker
  const selectedDate = value ? new Date(value + 'T00:00:00') : null
  const minDateObj = minDate ? new Date(minDate + 'T00:00:00') : undefined
  const maxDateObj = maxDate ? new Date(maxDate + 'T00:00:00') : undefined

  const handleChange = useCallback((date: Date | null) => {
    if (date) {
      // Convert back to ISO string (YYYY-MM-DD)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      onChange(`${year}-${month}-${day}`)
    } else {
      onChange('')
    }
  }, [onChange])

  return (
    <div className={`date-picker-container ${className}`}>
      <ReactDatePicker
        selected={selectedDate}
        onChange={handleChange}
        minDate={minDateObj}
        maxDate={maxDateObj}
        filterDate={filterDate}
        dateFormat="MM/dd/yyyy"
        placeholderText={placeholder}
        disabled={disabled}
        customInput={
          <CustomInput
            id={id}
            name={name}
            required={required}
            disabled={disabled}
            placeholder={placeholder}
          />
        }
        calendarClassName="custom-calendar"
        showPopperArrow={false}
        popperPlacement="bottom-start"
      />
    </div>
  )
}
